/**
 * Error Service - Centralized error handling
 * Provides consistent error handling across the application
 */

export interface AppError {
  code: string;
  message: string;
  details?: unknown;
}

export class ErrorService {
  /**
   * Handle Firebase errors with user-friendly messages
   */
  static handleFirebaseError(error: unknown): AppError {
    const firebaseError = error as { code: string; message: string };
    
    switch (firebaseError.code) {
      case 'auth/invalid-email':
        return {
          code: 'INVALID_EMAIL',
          message: 'Email inválido',
          details: firebaseError
        };
      case 'auth/user-disabled':
        return {
          code: 'USER_DISABLED',
          message: 'Usuario deshabilitado',
          details: firebaseError
        };
      case 'auth/user-not-found':
        return {
          code: 'USER_NOT_FOUND',
          message: 'Usuario no encontrado',
          details: firebaseError
        };
      case 'auth/wrong-password':
        return {
          code: 'WRONG_PASSWORD',
          message: 'Contraseña incorrecta',
          details: firebaseError
        };
      case 'auth/email-already-in-use':
        return {
          code: 'EMAIL_IN_USE',
          message: 'El email ya está en uso',
          details: firebaseError
        };
      case 'auth/operation-not-allowed':
        return {
          code: 'OPERATION_NOT_ALLOWED',
          message: 'Operación no permitida',
          details: firebaseError
        };
      case 'auth/weak-password':
        return {
          code: 'WEAK_PASSWORD',
          message: 'La contraseña es demasiado débil',
          details: firebaseError
        };
      case 'permission-denied':
        return {
          code: 'PERMISSION_DENIED',
          message: 'No tienes permisos para realizar esta acción',
          details: firebaseError
        };
      case 'unavailable':
        return {
          code: 'SERVICE_UNAVAILABLE',
          message: 'El servicio no está disponible. Intenta más tarde',
          details: firebaseError
        };
      default:
        return {
          code: 'UNKNOWN_ERROR',
          message: 'Error desconocido. Intenta nuevamente',
          details: firebaseError
        };
    }
  }

  /**
   * Handle generic errors
   */
  static handleGenericError(error: unknown): AppError {
    if (error instanceof Error) {
      return {
        code: 'GENERIC_ERROR',
        message: error.message,
        details: error
      };
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: 'Error desconocido',
      details: error
    };
  }

  /**
   * Log error for debugging
   */
  static logError(error: AppError, context?: string): void {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[${context || 'App'}] Error:`, error);
    }
  }
}
